import { useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { setNeoActiveOrgId } from 'src/components/Neo/neoStreamApi'
import { useUserContext } from 'src/context/user/UserContext'
import {
  selectPersonalOrganization,
  selectTeamOrganizations,
  useOrganizations,
  type OrganizationDto,
} from 'src/hooks/useOrganizations'
import { resolveActiveOrgId } from './resolveActiveOrgId'

const STORAGE_KEY_PREFIX = 'neo-active-org-id'
const EMPTY_ORGS: OrganizationDto[] = []

// Scope the remembered workspace to the signed-in user so a different account
// on the same browser never inherits the previous user's workspace.
function storageKeyForUser(userId: string | null): string {
  return userId ? `${STORAGE_KEY_PREFIX}:${userId}` : STORAGE_KEY_PREFIX
}

export type ActiveOrgContextValue = {
  organizations: OrganizationDto[]
  personalOrg: OrganizationDto | undefined
  teamOrgs: OrganizationDto[]
  activeOrg: OrganizationDto | undefined
  activeOrgId: string | null
  isPersonal: boolean
  isLoading: boolean
  switchOrg: (orgId: string) => void
}

const ActiveOrgContext = createContext<ActiveOrgContextValue | undefined>(
  undefined,
)

function readStoredOrgId(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStoredOrgId(key: string, orgId: string | null): void {
  try {
    if (orgId) {
      window.localStorage.setItem(key, orgId)
    } else {
      window.localStorage.removeItem(key)
    }
  } catch {
    // Storage can be unavailable (private mode); fall back to in-memory only.
  }
}

export function ActiveOrgProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const { data, isLoading } = useOrganizations()
  const queryClient = useQueryClient()
  const { state: userState } = useUserContext()
  const orgs = data ?? EMPTY_ORGS
  const storageKey = storageKeyForUser(userState.userProfile?.uid ?? null)

  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() => {
    const stored = readStoredOrgId(storageKey)
    // Seed the request interceptor synchronously so the very first org-scoped
    // query for a returning user already carries their last workspace.
    setNeoActiveOrgId(stored)
    return stored
  })

  // Every org-scoped Neo query carries X-Neo-Org-Id, so switching workspaces
  // must drop all neo* caches (chat, connections, dashboards, insights) to
  // avoid showing the previous workspace's data until stale time expires.
  const invalidateOrgScopedQueries = useCallback(() => {
    void queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0]
        return typeof key === 'string' && key.startsWith('neo')
      },
    })
  }, [queryClient])

  useEffect(() => {
    if (isLoading || orgs.length === 0) {
      return
    }
    const stillValid =
      activeOrgId !== null && orgs.some((org) => org.id === activeOrgId)
    if (stillValid) {
      setNeoActiveOrgId(activeOrgId)
      return
    }
    const resolved = resolveActiveOrgId(orgs, activeOrgId)
    setNeoActiveOrgId(resolved)
    setActiveOrgIdState(resolved)
    writeStoredOrgId(storageKey, resolved)
    invalidateOrgScopedQueries()
  }, [isLoading, orgs, activeOrgId, storageKey, invalidateOrgScopedQueries])

  const switchOrg = useCallback(
    (orgId: string) => {
      if (orgId === activeOrgId || !orgs.some((org) => org.id === orgId)) {
        return
      }
      setActiveOrgIdState(orgId)
      writeStoredOrgId(storageKey, orgId)
      setNeoActiveOrgId(orgId)
      invalidateOrgScopedQueries()
    },
    [activeOrgId, orgs, storageKey, invalidateOrgScopedQueries],
  )

  const value = useMemo<ActiveOrgContextValue>(() => {
    const activeOrg = orgs.find((org) => org.id === activeOrgId)
    const isResolving =
      isLoading || (orgs.length > 0 && activeOrg === undefined)
    return {
      organizations: orgs,
      personalOrg: selectPersonalOrganization(orgs),
      teamOrgs: selectTeamOrganizations(orgs),
      activeOrg,
      activeOrgId,
      isPersonal: activeOrg ? activeOrg.personalForUserId !== null : true,
      isLoading: isResolving,
      switchOrg,
    }
  }, [orgs, activeOrgId, isLoading, switchOrg])

  return (
    <ActiveOrgContext.Provider value={value}>
      {children}
    </ActiveOrgContext.Provider>
  )
}

export function useActiveOrg(): ActiveOrgContextValue {
  const ctx = useContext(ActiveOrgContext)
  if (!ctx) {
    throw new Error('useActiveOrg must be used within an ActiveOrgProvider')
  }
  return ctx
}
